import { type FC, type ReactNode } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import dynamic from 'next/dynamic';

import { Box, Button } from '@packages/shared';
import { ArrowMediumDown32 } from '@packages/themes/icons';

import type { SelectedFilterValue } from '../../types';
import type { Filter } from '../../api/rest/types';
import { useNestedFilterDialogs } from './useNestedFilterDialogs';

const FilterSettingsMobilePopovers = dynamic(
  () =>
    import(/* webpackChunkName: 'FilterSettingsMobilePopovers' */ './FilterSettingsMobilePopovers'),
  {
    ssr: false,
  },
);

export type FilterSettingsMobileProps = {
  /** Callback fired when a user changes the sorting order */
  onChangeSortingKey?: (key: string) => void;
  /** Callback fired when a user submits the selection of one or more filter values */
  onSubmit?: (filterValues: SelectedFilterValue[]) => void;
  /** Renders the content of the categories dialog, don't forget to invoke the closeDialog callback on selection */
  renderCategoryDialogContent: (closeDialog: () => void) => ReactNode;
  /** List of filters that should be listed for selection */
  filters: Filter[];
  /** The list of currently selected filter values */
  selectedFilterValues: SelectedFilterValue[];
  /** List of available sorting options for this product page */
  sortingOptions: { key: string; displayName: string }[];
  /** The currently selected sorting key */
  selectedSortingKey: string;
  /** Apply a workaround for a graphical glitch with the close button documented in https://github.com/mui/material-ui/issues/12879 */
  applyCloseButtonPaddingWorkaround?: boolean;
};

const messages = defineMessages({
  filterButton: {
    id: 'filters.selection.mobile.filterButton.text',
    defaultMessage: 'Filtern / Sortieren',
  },
  categoryButton: {
    id: 'filters.selection.mobile.categoryButton.text',
    defaultMessage: 'Kategorien',
  },
});

/**
 * A button that allows a user to select filter values on mobile using nested dialogs.
 *
 * @remarks
 * There can be multiple dialogs open at the same time
 * */
export const FilterSettingsMobile: FC<FilterSettingsMobileProps> = ({
  filters,
  selectedFilterValues: initiallySelectedFilterValues,
  sortingOptions,
  selectedSortingKey: initialSortingKey,
  onChangeSortingKey,
  onSubmit,
  renderCategoryDialogContent,
  applyCloseButtonPaddingWorkaround,
}) => {
  const { formatMessage } = useIntl();

  const {
    selectedFilterId,
    activeFilterIds,
    selectedFilterValues,
    hasFilterChanges,
    selectFilterValues,
    dialogControls,
  } = useNestedFilterDialogs({
    initiallySelectedFilterValues,
  });

  // ATTENTION: multiple dialogs can be open at the same time! Filter selection works like a 2-step wizard
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button
        sx={{
          flex: 1,
          height: '1.5rem',
          '& svg': {
            height: '2rem',
            width: '2rem',
          },
        }}
        endIcon={<ArrowMediumDown32 />}
        onClick={dialogControls.categoryDialog.open}
      >
        <Box
          sx={{
            width: '100%',
            marginRight: 'auto',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: 'left',
          }}
        >
          {formatMessage(messages.categoryButton)}
        </Box>
      </Button>
      <Button
        sx={{
          flex: 1,
          height: '1.5rem',
          '& svg': {
            height: '2rem',
            width: '2rem',
          },
        }}
        endIcon={<ArrowMediumDown32 />}
        onClick={dialogControls.filterDialog.open}
      >
        <Box
          sx={{
            width: '100%',
            marginRight: 'auto',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: 'left',
          }}
        >
          <FormattedMessage {...messages.filterButton} />
        </Box>
      </Button>

      <FilterSettingsMobilePopovers
        filters={filters}
        selectedFilterId={selectedFilterId}
        activeFilterIds={activeFilterIds}
        selectedFilterValues={selectedFilterValues}
        hasFilterChanges={hasFilterChanges}
        sortingOptions={sortingOptions}
        initialSortingKey={initialSortingKey}
        dialogControls={dialogControls}
        renderCategoryDialogContent={renderCategoryDialogContent}
        applyCloseButtonPaddingWorkaround={applyCloseButtonPaddingWorkaround}
        onChange={selectFilterValues}
        onChangeSortingKey={onChangeSortingKey}
        onSubmit={onSubmit}
      />
    </Box>
  );
};
